// Per-deal card data for the segregated portal one-pager (Stratus + Funder).
//
// One DealCardData per programme: identity, health, combined spend/budget, and the top
// 1-2 things to DO in that deal. To-dos come from each deal's OWN seed — open approvals
// (spend asks proposed/flagged, ranked oldest-age first) then open signals (Heimdall
// findings open + open/investigating leaks). There is NO schedule-slip line: slips are not
// structured data. Feasibility deals (no workspace seed) honestly have no to-dos.
//
// Kept pure where practical: deriveDealTodos / dealTodoComparator take their inputs and a
// `now`, so they are deterministic and testable; the hook binds the live store + clock.

import { useMemo } from "react";
import { useAllSeeds, useProgrammeSummaries, useProjectSummaries } from "@/domain/hooks";
import type { WorkspaceSeed } from "@/domain/types";
import type { ProgrammeSummary, ProjectSummary } from "@/domain/seed/stratus";

const HOUR_MS = 60 * 60 * 1000;

export type DealTodoKey = "approvals" | "signals";

export type DealTodo = {
  /** Stable key for the kind of work. */
  key: DealTodoKey;
  /** Noun-led label, e.g. "Open approvals". */
  label: string;
  /** How many items of this kind are waiting. */
  count: number;
  /** Age in hours of the oldest item (approvals only); undefined when not age-ranked. */
  ageHours?: number;
  /** Deep link into the deal, e.g. /workspace/:id/approvals. */
  href: string;
};

export type DealCardData = {
  id: string;
  name: string;
  /** Year span pulled from the programme name, e.g. "2026–2029"; undefined if none. */
  yearSpan?: string;
  phase: ProgrammeSummary["phase"];
  /** RAG health (programme.alertState) for the HealthPill. */
  health: ProgrammeSummary["alertState"];
  funder: string;
  budget: number;
  /** Summed from the deal's project summaries by workspaceId (0 for feasibility deals). */
  spent: number;
  workspaceId?: string;
  /** True when the deal has a real workspace and is clickable. */
  isActive: boolean;
  /** Top 1-2 to-dos, already ranked and truncated. Empty for feasibility deals. */
  todos: DealTodo[];
};

/** "Uganda Fire Stations 2026–2029" -> "2026–2029"; single year -> "2027". */
export function yearSpanFromName(name: string): string | undefined {
  const span = name.match(/20\d{2}\s*[–-]\s*20\d{2}/);
  if (span) return span[0].replace(/\s*[–-]\s*/, "–");
  const single = name.match(/20\d{2}/);
  return single ? single[0] : undefined;
}

/** Age in hours from an ISO timestamp to `now`, floored at 0. */
function ageHoursOf(iso: string | undefined, now: number): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  return Math.max(0, (now - t) / HOUR_MS);
}

/**
 * The full ranked to-do list for one deal's seed (not yet truncated): open approvals
 * first (oldest-age first), then open signals. One deterministic comparator throughout.
 */
export function deriveDealTodos(seed: WorkspaceSeed, workspaceId: string, now: number): DealTodo[] {
  const open: DealTodo[] = [];

  // 1. Open approvals — spend asks awaiting a decision, ranked by the oldest waiting ask.
  const openApprovals = seed.spendRequests.filter((r) => r.state === "proposed" || r.state === "flagged");
  if (openApprovals.length > 0) {
    const oldestAge = openApprovals.reduce<number | undefined>((max, r) => {
      const age = ageHoursOf(r.submittedAt, now);
      if (age == null) return max;
      return max == null ? age : Math.max(max, age);
    }, undefined);
    open.push({
      key: "approvals",
      label: "Open approvals",
      count: openApprovals.length,
      ageHours: oldestAge,
      href: `/workspace/${workspaceId}/approvals`,
    });
  }

  // 2. Open signals — Heimdall findings (open) + reconciliation leaks (open/investigating).
  // Exceptions are a materiality-config-derived view that already subsumes leaks, so we
  // count the distinct, config-free signal sources straight from the seed to stay honest.
  // There is no per-workspace signals route; findings + leaks are reviewed against their
  // asks on the approvals surface, so the deep link lands there (no 404, real destination).
  const openFindings = (seed.aiFindings ?? []).filter((f) => f.status === "open").length;
  const openLeaks = (seed.leaks ?? []).filter((l) => l.state === "open" || l.state === "investigating").length;
  const openSignals = openFindings + openLeaks;
  if (openSignals > 0) {
    open.push({
      key: "signals",
      label: "Open signals",
      count: openSignals,
      href: `/workspace/${workspaceId}/approvals`,
    });
  }

  return open.sort(dealTodoComparator);
}

/**
 * Deterministic ranking for a deal's to-dos: approvals before signals, then within a kind
 * the older (higher ageHours) first, then higher count, then key for a stable tie-break.
 */
export function dealTodoComparator(a: DealTodo, b: DealTodo): number {
  const KIND_RANK: Record<DealTodoKey, number> = { approvals: 0, signals: 1 };
  if (KIND_RANK[a.key] !== KIND_RANK[b.key]) return KIND_RANK[a.key] - KIND_RANK[b.key];
  const ageA = a.ageHours ?? -1;
  const ageB = b.ageHours ?? -1;
  if (ageA !== ageB) return ageB - ageA;
  if (a.count !== b.count) return b.count - a.count;
  return a.key.localeCompare(b.key);
}

/**
 * Cross-deal fast-path totals for the portal control row: open approvals and open signals
 * summed over every seed, using the SAME definitions as deriveDealTodos so the chip counts
 * and the per-card counts always agree.
 */
export type CrossDealTotals = { approvals: number; signals: number };

export function crossDealTotals(seeds: WorkspaceSeed[]): CrossDealTotals {
  return seeds.reduce<CrossDealTotals>(
    (acc, seed) => {
      acc.approvals += seed.spendRequests.filter((r) => r.state === "proposed" || r.state === "flagged").length;
      const openFindings = (seed.aiFindings ?? []).filter((f) => f.status === "open").length;
      const openLeaks = (seed.leaks ?? []).filter((l) => l.state === "open" || l.state === "investigating").length;
      acc.signals += openFindings + openLeaks;
      return acc;
    },
    { approvals: 0, signals: 0 },
  );
}

/** Hook form: cross-deal approvals + signals totals bound to the live store. */
export function useCrossDealTotals(): CrossDealTotals {
  const seeds = useAllSeeds();
  return useMemo(() => crossDealTotals(seeds), [seeds]);
}

/** Sum of project spend for a deal, from the cross-deal project summaries. */
export function spentForDeal(workspaceId: string | undefined, projects: ProjectSummary[]): number {
  if (!workspaceId) return 0;
  return projects.filter((p) => p.workspaceId === workspaceId).reduce((sum, p) => sum + p.spent, 0);
}

/** Build one deal's card data. Top 2 to-dos only; feasibility deals get none. */
export function buildDealCard(
  programme: ProgrammeSummary,
  seedsById: Record<string, WorkspaceSeed>,
  projects: ProjectSummary[],
  now: number,
): DealCardData {
  const workspaceId = programme.workspaceId;
  const seed = workspaceId ? seedsById[workspaceId] : undefined;
  const isActive = Boolean(workspaceId && seed);
  const todos = isActive ? deriveDealTodos(seed!, workspaceId!, now).slice(0, 2) : [];
  return {
    id: programme.id,
    name: programme.name,
    yearSpan: yearSpanFromName(programme.name),
    phase: programme.phase,
    health: programme.alertState,
    funder: programme.funder,
    budget: programme.totalBudget,
    spent: spentForDeal(workspaceId, projects),
    workspaceId,
    isActive,
    todos,
  };
}

/**
 * One DealCardData per programme, in portfolio order. Active deals (real workspaceId) carry
 * real to-do counts derived from their own seed; feasibility deals carry none. Pure builders
 * above are exported for tests; this hook just binds the live store and clock.
 */
export function useDealCards(now: number = Date.now()): DealCardData[] {
  const programmes = useProgrammeSummaries();
  const seeds = useAllSeeds();
  const projects = useProjectSummaries();
  return useMemo(() => {
    const seedsById = Object.fromEntries(seeds.map((s) => [s.workspace.id, s] as const));
    return programmes.map((p) => buildDealCard(p, seedsById, projects, now));
  }, [programmes, seeds, projects, now]);
}
